/*global Dingdocs*/

import { useEffect, useState, useCallback } from 'react';
import { initView } from 'dingtalk-docs-cool-app';
import InterviewForm from './InterviewForm';
import InterviewFormBack from './InterviewFormBack';
import {
  mapRecordsToForm,
  extractCellValue,
  getSubTableStats,
  type FormData,
} from './fieldMapping';
import './interview-form.css';

const JSAPI_LIST = ['DingdocsScript.base.readWriteAll'];

/** 调用本地鉴权服务，获取 Script API 读写权限（行政区域等 object 字段依赖此项） */
async function initConfigPermission(): Promise<void> {
  const currentUrl = window.location.href.split('#')[0];
  const response = await fetch(
    `/api/configPermission?url=${encodeURIComponent(currentUrl)}`,
  );
  if (!response.ok) {
    throw new Error(`鉴权服务请求失败: ${response.status} ${response.statusText}`);
  }

  const configData = await response.json();
  if (configData.error) {
    throw new Error(configData.error);
  }

  await Dingdocs.base.host.configPermission(
    configData.agentId,
    configData.corpId,
    configData.timeStamp,
    configData.nonceStr,
    configData.signature,
    configData.jsApiList ?? JSAPI_LIST,
  );
}

function App() {
  const [ready, setReady] = useState(false);
  const [permissionOk, setPermissionOk] = useState(false);
  const [formData, setFormData] = useState<FormData>({});
  const [rawFields, setRawFields] = useState<Record<string, unknown>>({});
  const [recordId, setRecordId] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [recordName, setRecordName] = useState('');
  const [relatedStats, setRelatedStats] = useState<{
    totalRecords: number;
    relatedRecords: number;
    family: number;
    work: number;
    edu: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadRecord = useCallback(async (
    id: string,
    viewId?: string | null,
    activeSheetId?: string | null,
  ) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const bundle = await Dingdocs.script.run(
        'getApplicantBundle',
        id,
        activeSheetId ?? undefined,
        viewId ?? undefined,
      );
      if (bundle?.primary) {
        const record = bundle.primary;
        const relatedFields = (bundle.related ?? []).map(
          (item: { fields: Record<string, unknown> }) => item.fields,
        );
        const mapped = mapRecordsToForm(record.fields, relatedFields);
        const subStats = getSubTableStats(mapped);
        setFormData(mapped);
        setRawFields(record.fields ?? {});
        setRecordId(record.id);
        setViewId(viewId ?? record._meta?.viewId ?? null);
        setSheetId(activeSheetId ?? record._meta?.sheetId ?? null);
        const name = extractCellValue(record.fields['姓名'] ?? record.fields['name']);
        setRecordName(name);
        setRelatedStats({
          totalRecords: bundle.stats?.totalRecords ?? 1,
          relatedRecords: bundle.stats?.relatedRecords ?? 0,
          ...subStats,
        });
      } else {
        setError('未读取到记录数据，请确认脚本服务已启动，并点击表格中的单元格（如「姓名」列）');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`加载记录失败: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let unsubscribers: Array<() => void> = [];

    initView({
      onReady: () => {
        setReady(true);

        // 鉴权与数据加载并行：仅启动时执行一次，不阻塞选中行加载
        initConfigPermission()
          .then(() => {
            setPermissionOk(true);
            console.log('[面试登记表] configPermission 鉴权成功');
          })
          .catch((permErr) => {
            console.error('[面试登记表] configPermission 鉴权失败:', permErr);
            setError(
              '插件鉴权失败，户籍地址/现居住址等行政区域字段可能无法读取。'
              + '请确认：1) 已运行 npm run start:server；'
              + '2) local_server/.env.server 配置正确；'
              + '3) 钉钉开放平台已开通 AI 表格读权限。',
            );
          });

        // 监听选中行变化，自动加载对应记录
        const offSelection = Dingdocs.base.event.onSelectionChanged((selection) => {
          if (selection.viewId) setViewId(selection.viewId);
          if (selection.sheetId) setSheetId(selection.sheetId);
          if (selection.recordId) {
            loadRecord(selection.recordId, selection.viewId, selection.sheetId);
          }
        });
        unsubscribers.push(offSelection);

        // 插件打开时若已有选中单元格，补载一次（onSelectionChanged 不会重复触发）
        Dingdocs.script.run('getCurrentSelection')
          .then((selection: { recordId?: string; viewId?: string; sheetId?: string } | null) => {
            if (selection?.recordId) {
              loadRecord(selection.recordId, selection.viewId, selection.sheetId);
            }
          })
          .catch((err: unknown) => {
            console.warn('[面试登记表] 读取当前选中失败（脚本服务未就绪？）:', err);
          });

        // 监听记录修改，实时刷新
        const offModified = Dingdocs.base.event.onRecordModified((event) => {
          if (event.recordId && event.recordId === recordId) {
            loadRecord(event.recordId, viewId, sheetId);
          }
        });
        unsubscribers.push(offModified);
      },
      onError: (e) => {
        console.error('initView error:', e);
        setError('插件初始化失败，请刷新重试');
      },
    });

    return () => {
      unsubscribers.forEach((fn) => {
        try { fn(); } catch { /* ignore */ }
      });
    };
  }, [loadRecord, recordId, viewId, sheetId]);

  const handlePrint = () => {
    window.print();
  };

  const handleRefresh = async () => {
    if (recordId) {
      await loadRecord(recordId, viewId, sheetId);
    }
  };

  if (!ready) {
    return <div className="form-empty-hint">插件加载中...</div>;
  }

  return (
    <div className="interview-form-wrapper">
      <div className="form-toolbar no-print">
        <button onClick={handleRefresh} disabled={!recordId || loading}>
          {loading ? '加载中...' : '刷新'}
        </button>
        <button onClick={handlePrint} disabled={!recordId}>
          打印
        </button>
        {recordName && (
          <span style={{ lineHeight: '28px', color: '#666' }}>
            当前: {recordName}
            {relatedStats && (
              <span style={{ marginLeft: 8 }}>
                （家庭 {relatedStats.family} 条 · 工作 {relatedStats.work} 条 · 教育 {relatedStats.edu} 条
                {relatedStats.relatedRecords > 0 && `，合并 ${relatedStats.relatedRecords + 1} 行记录`}）
              </span>
            )}
          </span>
        )}
        {ready && permissionOk && (
          <span style={{ lineHeight: '28px', color: '#52c41a', fontSize: 12 }}>鉴权已就绪</span>
        )}
      </div>

      {error && (
        <div className="form-empty-hint" style={{ color: '#ff4d4f' }}>{error}</div>
      )}

      {!recordId && !loading && !error && (
        <div className="form-empty-hint">
          请在「收集结果」视图中<strong>点击某一行的单元格</strong>（如「姓名」列），不要只点行号。
          表单将自动显示该求职者的家庭/工作/教育经历。若已选中仍无数据，请先点其他行再点回来，或点「刷新」。
        </div>
      )}

      {recordId && (
        <>
          <InterviewForm data={formData} />
          <InterviewFormBack data={formData} rawFields={rawFields} />
        </>
      )}
    </div>
  );
}

export default App;
